import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useAdvisorConfig, useUpdateAdvisorConfig, useUploadAdvisorPhoto, useUploadCompanyLogo } from '../../../hooks/useAdvisorConfig';
import ImageUploader from './ImageUploader';

interface AdvisorProfileInfoEditorProps {
  selectedCustomerId: string;
  onCustomerIdChange?: (customerId: string) => void;
}

/**
 * Advisor Profile Information Editor
 *
 * For editing basic profile information:
 * - Name, Company, Email, Phone
 * - Advisor Photo
 * - Company Logo
 *
 * Used in External Webform Configuration tab.
 */
const AdvisorProfileInfoEditor: React.FC<AdvisorProfileInfoEditorProps> = ({
  selectedCustomerId
}) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Hooks
  const { config, loading: configLoading, error: configError, refresh } = useAdvisorConfig(
    selectedCustomerId || null,
    true
  );
  const { update, updating, error: updateError } = useUpdateAdvisorConfig();
  const photoUpload = useUploadAdvisorPhoto();
  const logoUpload = useUploadCompanyLogo();

  // Load config data into form
  useEffect(() => {
    if (config) {
      setFormData({
        name: config.name || '',
        company: config.company || '',
        email: config.email || '',
        phone: config.phone || ''
      });
    }
  }, [config]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    if (!selectedCustomerId) return;

    try {
      setSuccessMessage(null);

      // Prepare update payload
      const updates: any = {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone
      };

      await update(selectedCustomerId, updates);
      setSuccessMessage('Profile updated successfully!');
      refresh();
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handlePhotoUpload = async (file: File): Promise<string | null> => {
    if (!selectedCustomerId) return null;
    const url = await photoUpload.upload(selectedCustomerId, file);
    if (url) {
      refresh();
    }
    return url;
  };

  const handleLogoUpload = async (file: File): Promise<string | null> => {
    if (!selectedCustomerId) return null;
    const url = await logoUpload.upload(selectedCustomerId, file);
    if (url) {
      refresh();
    }
    return url;
  };

  if (!selectedCustomerId) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Please select a customer ID to edit profile information.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Loading State */}
      {configLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {configError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {configError}
        </Alert>
      )}

      {/* Form */}
      {!configLoading && selectedCustomerId && (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Profile Information
            </Typography>

            <Box sx={{ mt: 2 }}>
            {/* Name */}
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              margin="normal"
              disabled={updating}
            />

            {/* Company */}
            <TextField
              fullWidth
              label="Company"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              margin="normal"
              disabled={updating}
            />

            {/* Email */}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              margin="normal"
              disabled={updating}
            />

            {/* Phone */}
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              margin="normal"
              disabled={updating}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Images */}
          <Typography variant="h6" gutterBottom>
            Images
          </Typography>

          <Box sx={{ mt: 2 }}>
            <ImageUploader
              label="Advisor Photo"
              currentImageUrl={config?.advisorPhotoUrl}
              onUpload={handlePhotoUpload}
              uploading={photoUpload.uploading}
              progress={photoUpload.progress}
              error={photoUpload.error}
              helperText="JPG, PNG, or WEBP. Max 5MB."
            />

            <ImageUploader
              label="Company Logo"
              currentImageUrl={config?.companyLogoUrl}
              onUpload={handleLogoUpload}
              uploading={logoUpload.uploading}
              progress={logoUpload.progress}
              error={logoUpload.error}
              helperText="JPG, PNG, or WEBP. Max 5MB."
              landscape
            />
          </Box>

          {/* Success Message */}
          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {successMessage}
            </Alert>
          )}

          {/* Update Error */}
          {updateError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updateError}
            </Alert>
          )}

          {/* Save Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={updating ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={updating || photoUpload.uploading || logoUpload.uploading}
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AdvisorProfileInfoEditor;
